import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { LearningResourceEnrollment } from 'src/app/models/learning_resource_enrollment.model';
import { LearningResource } from 'src/app/models/learning_resource.model';

export interface CompleteActiveEnrollmentPromptDialogData {
  active_enrollments: LearningResourceEnrollment[];
  new_learning_resource: LearningResource;
}

export type CompleteActiveEnrollmentPromptDialogResult =
  | { action: 'activate_only' }
  | { action: 'close_selected'; enrollment_ids: number[] }
  | { action: 'complete_selected'; enrollment_ids: number[] };

@Component({
  selector: 'app-complete-active-enrollment-prompt-dialog',
  templateUrl: './complete-active-enrollment-prompt-dialog.component.html',
  styleUrl: './complete-active-enrollment-prompt-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogTitle, MatDialogContent, MatDialogActions, MatButtonModule, MatCheckboxModule, MatIconModule, MatMenuModule],
})
export class CompleteActiveEnrollmentPromptDialogComponent {
  private readonly dialog_ref = inject(
    MatDialogRef<CompleteActiveEnrollmentPromptDialogComponent, CompleteActiveEnrollmentPromptDialogResult | undefined>
  );

  readonly data = inject<CompleteActiveEnrollmentPromptDialogData>(MAT_DIALOG_DATA);

  readonly new_resource_name = this.data.new_learning_resource.name;

  readonly selected_enrollment_ids = signal<ReadonlySet<number>>(new Set(this.data.active_enrollments.map(enrollment => enrollment.id)));

  readonly active_enrollment_labels = this.data.active_enrollments.map(enrollment => ({
    id: enrollment.id,
    name: enrollment.learning_resource?.name ?? `Matrícula #${enrollment.id}`,
  }));

  activateWithoutClosing(): void {
    this.dialog_ref.close({ action: 'activate_only' });
  }

  closeSelected(): void {
    const enrollment_ids = [...this.selected_enrollment_ids()];

    if (enrollment_ids.length === 0) {
      return;
    }

    this.dialog_ref.close({ action: 'close_selected', enrollment_ids });
  }

  completeSelected(): void {
    const enrollment_ids = [...this.selected_enrollment_ids()];

    if (enrollment_ids.length === 0) {
      return;
    }

    this.dialog_ref.close({ action: 'complete_selected', enrollment_ids });
  }

  cancel(): void {
    this.dialog_ref.close(undefined);
  }

  toggleEnrollmentSelection(enrollment_id: number, checked: boolean): void {
    this.selected_enrollment_ids.update(current_ids => {
      const next_ids = new Set(current_ids);

      if (checked) {
        next_ids.add(enrollment_id);
      } else {
        next_ids.delete(enrollment_id);
      }

      return next_ids;
    });
  }

  isEnrollmentSelected(enrollment_id: number): boolean {
    return this.selected_enrollment_ids().has(enrollment_id);
  }
}
